import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Mail, MapPin } from 'lucide-react';
import { COMPANY } from '../../data/content';

export const ContactCTA = ({ onBook }) => {
  return (
    <section
      id="contact"
      className="relative py-24 md:py-40 bg-[#102234] text-white overflow-hidden"
      data-testid="contact-section"
    >
      <div className="absolute top-1/2 left-0 w-[700px] h-[700px] -translate-y-1/2 rounded-full bg-[#C9A84C]/20 blur-[150px] pointer-events-none" />
      <div className="absolute top-0 right-1/4 w-[400px] h-[400px] rounded-full bg-[#F9F7F4]/10 blur-[120px] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20">
          {/* Big CTA */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-6"
            >
              <div className="h-px w-12 bg-[#C9A84C]" />
              <span className="text-[#C9A84C] text-xs tracking-[0.4em] uppercase">
                Begin
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-serif leading-[0.95] tracking-tight"
              style={{ fontSize: 'clamp(3rem, 8vw, 7rem)' }}
            >
              Ready for
              <br />
              <span className="italic font-light text-[#C9A84C]">spotless?</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 text-lg text-white/60 font-light max-w-md leading-relaxed"
            >
              Tell us about your space. A senior team member will respond within
              two hours with a tailored quote.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={onBook}
                className="group relative overflow-hidden bg-[#C9A84C] text-[#0D1B2A] px-10 py-5 text-xs tracking-[0.3em] uppercase font-semibold flex items-center justify-center gap-3"
                data-testid="cta-book-btn"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-[#D9BC6E] to-[#C9A84C] -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
                <span className="relative">Request Quote</span>
                <ArrowRight className="relative w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="tel:5712656239"
                className="border border-white/20 text-white hover:border-[#C9A84C] hover:text-[#C9A84C] px-10 py-5 text-xs tracking-[0.3em] uppercase flex items-center justify-center gap-3 transition-colors"
                data-testid="cta-call-btn"
              >
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </motion.div>
          </div>

          {/* Contact details */}
          <div className="md:col-span-4 md:col-start-9">
            <div className="border-t border-white/10 pt-8">
              <div className="text-xs tracking-[0.3em] uppercase text-[#C9A84C] mb-3">Phone</div>
              {COMPANY.phones.map((p) => (
                <a
                  key={p}
                  href={`tel:${p.replace(/\D/g, '')}`}
                  className="block font-serif text-2xl md:text-3xl text-white hover:text-[#C9A84C] transition-colors mb-1"
                  data-testid={`contact-phone-${p.replace(/\D/g, '')}`}
                >
                  {p}
                </a>
              ))}
            </div>

            <div className="border-t border-white/10 pt-8 mt-8">
              <div className="text-xs tracking-[0.3em] uppercase text-[#C9A84C] mb-3">Email</div>
              <a
                href={`mailto:${COMPANY.email}`}
                className="font-serif text-lg md:text-xl text-white hover:text-[#C9A84C] transition-colors break-all"
                data-testid="contact-email"
              >
                {COMPANY.email}
              </a>
            </div>

            <div className="border-t border-white/10 pt-8 mt-8">
              <div className="text-xs tracking-[0.3em] uppercase text-[#C9A84C] mb-3">Service Area</div>
              <p className="font-serif text-lg text-white/80">
                {COMPANY.serviceArea}
              </p>
              <p className="text-sm text-white/60 mt-2">
                Arlington · Alexandria · Bethesda · Silver Spring · Fairfax · Reston
              </p>
            </div>

            <div className="border-t border-white/10 pt-8 mt-8">
              <div className="text-xs tracking-[0.3em] uppercase text-[#C9A84C] mb-3">Hours</div>
              <p className="font-serif text-lg text-white/80">Mon – Sat · 6am – 10pm</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
